import React from 'react';
import { Typography, Box, useTheme } from '@mui/material';
import { getRolesByRegion } from '../../data/roles';
import { formatCurrency } from '../../utils/formatCurrency';
import { useCurrency } from '../../contexts/CurrencyContext';

export default function LiveCostCalculator({ 
  attendees, 
  elapsedSeconds, 
  showBreakdown = false, 
  showDuration = false 
}) {
  const theme = useTheme();
  const { currency, region } = useCurrency();
  const roles = getRolesByRegion(region);
  
  // Calculate total hourly rate
  const totalHourlyRate = roles.reduce((total, role) => {
    const count = attendees[role.key] || 0;
    return total + (role.hourlyRate * count);
  }, 0);

  // Calculate current cost based on elapsed time
  const hours = elapsedSeconds / 3600;
  const currentCost = totalHourlyRate * hours;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) {
      return `${secs} seconds`;
    }
    return `${mins} minutes ${secs} seconds`;
  };

  return (
    <Box data-testid="live-cost" sx={{ textAlign: 'center' }}>
      <Typography
        variant="h1"
        sx={{
          fontFamily: 'monospace',
          fontWeight: 'bold',
          color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
          mb: 1,
          fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
          letterSpacing: 1,
        }}
      >
        {formatCurrency(currentCost, currency)}
      </Typography>

      {showBreakdown && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {formatCurrency(totalHourlyRate, currency)} per hour
        </Typography>
      )}

      {showDuration && (
        <Typography variant="body2" color="text.secondary">
          {formatDuration(elapsedSeconds)}
        </Typography>
      )}
    </Box>
  );
}